import { useEffect, useState } from "react";
import { MIN_PLAYERS, MAX_PLAYERS, MaintenanceState } from "@badorbad/shared";
import { useMaintenance } from "../design/useMaintenance";
import { AvatarFace } from "../design/AvatarFace";
import { Sheet } from "../design/Sheet";
import { nameColorFor } from "../design/seed";
import { NAME_COLORS, palette } from "../design/tokens";
import { t, fmt } from "../locale";
import { useGame, selectIsHost, type Player } from "../stores/game";

/**
 * The room between games. Everything here is host-gated except leaving, and the
 * host's powers are all reachable from one place — tapping a player — rather
 * than from per-row buttons that would double the width of every line.
 */

/** Only meaningful for a room the client can actually be sent a link to. */
const roomLink = (code: string) => `${window.location.origin}/r/${code}`;

/** Which of the two copy targets was last taken, so only that one confirms. */
enum Copied {
  Code = "code",
  Link = "link",
}

const PlayerRow = ({
  player,
  isMe,
  canManage,
  onManage,
}: {
  player: Player;
  isMe: boolean;
  canManage: boolean;
  onManage: () => void;
}) => {
  const body = (
    <>
      <div className={player.connected ? undefined : "opacity-40"}>
        <AvatarFace seed={player.avatar_seed} size={44} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-start">
        <span
          className="w-full truncate text-left text-base font-bold"
          style={{ color: nameColorFor(player.avatar_seed, NAME_COLORS) }}
        >
          {player.display_name}
        </span>
        <span className="text-xs text-ink-dim">
          {[
            player.is_host ? t.lobby.host : null,
            isMe ? t.lobby.you : null,
            player.connected ? null : t.lobby.disconnected,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>
    </>
  );

  if (!canManage) {
    return (
      <div className="flex w-full items-center gap-3 bg-surface px-3 py-2">{body}</div>
    );
  }

  return (
    <button
      className="flex w-full items-center gap-3 bg-surface px-3 py-2 text-left"
      aria-label={fmt(t.lobby.manage, player.display_name)}
      onClick={onManage}
    >
      {body}
    </button>
  );
};

export const Lobby = () => {
  const roomCode = useGame((s) => s.roomCode);
  const players = useGame((s) => s.players);
  const myUserId = useGame((s) => s.myUserId);
  const isHost = useGame(selectIsHost);
  const error = useGame((s) => s.error);
  const startGame = useGame((s) => s.startGame);
  const kick = useGame((s) => s.kick);
  const transferHost = useGame((s) => s.transferHost);
  const leave = useGame((s) => s.leave);

  const [managed, setManaged] = useState<Player | null>(null);
  const [copied, setCopied] = useState<Copied | null>(null);

  const maintenance = useMaintenance();

  /*
   * Once the drain is over the lobby stops existing, so anyone sitting in one
   * is returned home. A game already running is untouched — those players are
   * on Setup/Turn/Reveal/Results, never here — and reach this screen only when
   * their game ends, at which point they are shown out too. That is the whole
   * "play it out, then leave" rule, and it needs no separate machinery.
   */
  useEffect(() => {
    if (maintenance.state === MaintenanceState.Active) leave();
  }, [maintenance.state, leave]);

  if (!roomCode) return null;

  const missing = MIN_PLAYERS - players.length;
  // The server refuses a start during maintenance regardless; disabling the
  // button is only so the host is not invited into a guaranteed refusal.
  const canStart =
    isHost &&
    players.length >= MIN_PLAYERS &&
    maintenance.state === MaintenanceState.Off;

  const copy = async (text: string, what: Copied) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 2_000);
    } catch {
      // Clipboard is unavailable over plain http on some browsers. The code is
      // on screen regardless, which is the fallback the room code exists for.
    }
  };

  return (
    <div className="relative mx-auto flex min-h-full max-w-md flex-col px-5 pb-6 pt-8">
      <div className="flex flex-col items-center gap-1">
        {/* The label doubles as the confirmation, so nothing shifts on tap. */}
        <p className="text-xs uppercase tracking-widest text-ink-dim">
          {copied === Copied.Code ? t.lobby.copied : t.lobby.roomCode}
        </p>
        <button
          className="min-h-0 px-2 text-4xl font-bold uppercase tracking-[0.2em]"
          aria-label={t.lobby.copyCode}
          onClick={() => copy(roomCode, Copied.Code)}
        >
          {roomCode}
        </button>
        <button
          className="mt-2 min-h-0 px-3 py-2 text-sm text-ink-dim underline"
          onClick={() => copy(roomLink(roomCode), Copied.Link)}
        >
          {copied === Copied.Link ? t.lobby.copied : t.lobby.shareLink}
        </button>
      </div>

      <p className="mt-6 text-center text-xs uppercase tracking-widest text-ink-dim">
        {fmt(t.lobby.playerCount, String(players.length), String(MAX_PLAYERS))}
      </p>

      <ul className="mt-3 flex flex-1 flex-col gap-2">
        {players.map((p) => (
          <li key={p.user_id}>
            <PlayerRow
              player={p}
              isMe={p.user_id === myUserId}
              canManage={isHost && p.user_id !== myUserId}
              onManage={() => setManaged(p)}
            />
          </li>
        ))}
      </ul>

      {error && <p className="mt-4 text-center text-sm text-side-b">{error}</p>}

      <div className="mt-6 flex flex-col gap-3">
        {isHost ? (
          <>
            <button
              className="w-full bg-side-a px-4 py-4 text-base font-bold uppercase tracking-tight text-bg disabled:opacity-40"
              disabled={!canStart}
              onClick={startGame}
            >
              {t.lobby.start}
            </button>
            {maintenance.state !== MaintenanceState.Off && (
              <p className="text-center text-sm text-ink-dim">
                {t.connection.maintenanceLobby}
              </p>
            )}
            {missing > 0 && maintenance.state === MaintenanceState.Off && (
              <p className="text-center text-sm text-ink-dim">
                {fmt(t.lobby.needMorePlayers, String(missing))}
              </p>
            )}
          </>
        ) : (
          <p className="text-center text-sm text-ink-dim">{t.lobby.waitingForHost}</p>
        )}

        <button
          className="min-h-0 px-4 py-3 text-sm text-ink-dim"
          onClick={leave}
        >
          {t.lobby.leave}
        </button>
      </div>

      <Sheet
        open={managed !== null}
        onClose={() => setManaged(null)}
        title={managed?.display_name ?? ""}
      >
        <div className="flex flex-col gap-2 pb-2">
          {/* Offered only for someone actually present — the server refuses a
              transfer to an absent player, and a control that always fails is
              worse than one that is not there. */}
          {managed?.connected && (
            <button
              className="w-full bg-surface px-4 py-3 text-left text-base"
              onClick={() => {
                transferHost(managed.user_id);
                setManaged(null);
              }}
            >
              {t.lobby.makeHost}
            </button>
          )}
          <button
            className="w-full bg-surface px-4 py-3 text-left text-base"
            style={{ color: palette.sideB }}
            onClick={() => {
              if (managed) kick(managed.user_id);
              setManaged(null);
            }}
          >
            {t.lobby.kick}
          </button>
        </div>
      </Sheet>
    </div>
  );
};
